/**
 * RequestClosureDialog — the customer asks FinminOS to close a service.
 *
 * Reasons come from `GET /service-closure/reasons` (codes are sent, labels are
 * shown). `customer_other` requires a description; the schema is built from
 * the catalog so that rule is never duplicated client-side. On success the SR
 * is refetched by the mutation hook and the parent re-renders from the
 * authoritative `service_closure` object.
 */
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Check, Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorMessage,
  Skeleton,
  Textarea,
  toast,
} from '@finmin/ui';
import {
  closureErrorMessage,
  useClosureReasons,
  useCreateClosureRequest,
  type ServiceClosureReason,
} from '@/api/service-closure';
import { PALETTE_FOREST } from '../tokens';

const DESCRIPTION_MAX = 2000;

function buildSchema(reasons: ServiceClosureReason[]) {
  return z
    .object({
      reason_code: z.string().min(1, 'Please choose a reason'),
      description: z.string().max(DESCRIPTION_MAX, `Keep it under ${DESCRIPTION_MAX} characters`),
    })
    .superRefine((val, ctx) => {
      const reason = reasons.find((r) => r.code === val.reason_code);
      if (reason?.description_required && !val.description.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['description'],
          message: 'Please tell us a little more about why you want to close this service',
        });
      }
    });
}

type FormValues = { reason_code: string; description: string };
export type Input = FormValues;

interface Props {
  serviceRequestId: string;
  serviceTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RequestClosureDialog({ serviceRequestId, serviceTitle, open, onOpenChange }: Props) {
  const reasonsQuery = useClosureReasons({ enabled: open });
  const reasons = React.useMemo(() => reasonsQuery.data?.customer ?? [], [reasonsQuery.data]);
  const schema = React.useMemo(() => buildSchema(reasons), [reasons]);

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason_code: '', description: '' },
  });

  const create = useCreateClosureRequest({
    onSuccess: () => {
      toast.success('Closure request sent', 'Our team will review it within five working days.');
      onOpenChange(false);
    },
  });

  // Reset transient state every time the dialog is (re)opened.
  React.useEffect(() => {
    if (open) {
      reset({ reason_code: '', description: '' });
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedCode = watch('reason_code');
  const selected = reasons.find((r) => r.code === selectedCode);
  const description = watch('description');
  const busy = create.isPending;

  function onSubmit(values: FormValues) {
    const trimmed = values.description.trim();
    create.mutate({
      serviceRequestId,
      payload: {
        reason_code: values.reason_code,
        description: trimmed ? trimmed : null,
      },
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request service closure</DialogTitle>
          <DialogDescription>
            Ask FinminOS to close your {serviceTitle}. Work pauses while our team reviews
            your request, and you can withdraw it at any time before a decision.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {/* Reason picker — a single-select list styled as choice rows. */}
          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-foreground">Why do you want to close it?</legend>
            {reasonsQuery.isLoading ? (
              <div className="space-y-2 pt-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-xl" />
                ))}
              </div>
            ) : reasonsQuery.isError ? (
              <ErrorMessage message="Could not load closure reasons. Please try again." />
            ) : (
              <Controller
                control={control}
                name="reason_code"
                render={({ field }) => (
                  <div role="radiogroup" aria-label="Closure reason" className="space-y-1.5 pt-1">
                    {reasons.map((r) => {
                      const active = field.value === r.code;
                      return (
                        <button
                          key={r.code}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          disabled={busy}
                          onClick={() => field.onChange(r.code)}
                          className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-sm transition-colors disabled:opacity-60 ${
                            active
                              ? 'border-primary bg-primary/5 text-foreground'
                              : 'border-border text-foreground hover:bg-muted/50'
                          }`}
                        >
                          <span>{r.label}</span>
                          {active && <Check className="h-4 w-4" style={{ color: PALETTE_FOREST }} aria-hidden />}
                        </button>
                      );
                    })}
                  </div>
                )}
              />
            )}
            {errors.reason_code && (
              <p className="text-xs text-destructive">{errors.reason_code.message}</p>
            )}
          </fieldset>

          {/* Description — optional unless the selected reason requires it. */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label htmlFor="closure-description" className="text-sm font-medium text-foreground">
                Anything else we should know?
                {selected?.description_required ? (
                  <span className="text-destructive"> *</span>
                ) : (
                  <span className="text-muted-foreground"> (optional)</span>
                )}
              </label>
              <span className="text-xs text-muted-foreground">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </div>
            <Textarea
              id="closure-description"
              rows={3}
              maxLength={DESCRIPTION_MAX}
              placeholder="Share any context that helps our team review this quickly."
              aria-invalid={!!errors.description}
              disabled={busy}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-xs text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Server-authoritative error (409 already pending, 409 after payment, …) */}
          <ErrorMessage
            message={
              create.error
                ? closureErrorMessage(create.error, 'Could not send your request. Please try again.')
                : null
            }
          />

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
              Keep service
            </Button>
            <Button type="submit" size="sm" disabled={busy || reasons.length === 0}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
              {busy ? 'Sending…' : 'Request closure'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
